import { useWallet } from "@solana/wallet-adapter-react";
import { useFormikContext } from "formik";
import { FC, Fragment, useContext, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import removeIcon from "../../../assets/removeImg.svg";
import { updateUserRequest } from "../../../api/users.api";
import {
  MAX_IMAGE_SIZE,
  MESSAGE_TYPE,
} from "../../../common/constants/common.constants";
import {
  IResendEmailDto,
  IUserAccount,
  IUserSettingsFormFields,
} from "../../../common/interfaces/user.interface";
import { UserContext } from "../../../contexts/userContext";
import {
  copyWalletAddress,
  getTrimmedPublicKey,
  signTransactionMessage,
  uploadImage,
} from "../../../utilities/helpers";
import { createNotification } from "../../../utilities/notification";
import countryList from "../../../assets/countries.json";
import WizardField from "../../WizardField/WizardField";
import "./BasicInfo.scss";
import copyIcon from "../../../assets/copy.svg";
import { ReactComponent as LinkIcon } from "../../../assets/link2.svg";
import { useGateway } from "@civic/solana-gateway-react";
import VerificationModal from "../VerificationModal/VerificationModal";
import clubAvatar from "../../../assets/clubAvatar.png";
import CustomCheckbox from "../../CustomCheckbox/CustomCheckbox";
import { IOption } from "../../../common/interfaces/common.interface";

export interface IBasicInfoProps {
  isModal: boolean;
  emailField: {
    name: string;
    label: string;
    initialValue: string;
  };
  usernameField: {
    name: string;
    label: string;
    initialValue: string;
  };
  userImageField: {
    name: string;
    label: string;
    initialValue: string;
  };
  country: {
    name: string;
    label: string;
    initialValue: string;
  };
}

const BasicInfo: FC<IBasicInfoProps> = ({
  emailField,
  usernameField,
  country,
  isModal,
}) => {
  const reader = new FileReader();
  const { requestGatewayToken, gatewayStatus, gatewayToken } = useGateway();

  const [verificationModal, setVerificationModal] = useState(false);

  const { setFieldValue, values } = useFormikContext<IUserSettingsFormFields>();
  const { user, updateUser, isWhitelisted, adminData } =
    useContext(UserContext);

  const [countries, setCountries] = useState<IOption[]>([]);

  const payerWallet = useWallet();
  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    noKeyboard: true,
    accept: "image/png, image/jpg, image/jpeg,image/gif",
    maxSize: MAX_IMAGE_SIZE,
    onDrop: (acceptedFiles) => {
      uploadImage(acceptedFiles, reader, "userImage", setFieldValue, true);
    },
  });

  useEffect(() => {
    if (user) {
      setFieldValue("username", user.name);
      setFieldValue("email", user.email);
      setFieldValue("userImage", user.imageUrl + `?${Math.random()}`);
      setFieldValue("country", user.country);
      setFieldValue("isLedger", user.isLedger);
      setFieldValue("discord", user.discord);
      (isWhitelisted || adminData) &&
        setFieldValue("editingWhitelistRequest", false);
    }
    setCountryList();
  }, [user]);

  useEffect(() => {
    if (gatewayToken) {
      setVerificationModal(false);
    }
  }, [gatewayToken]);

  const removeImage = async (e) => {
    e.stopPropagation();
    setFieldValue("userImage", "");
    setFieldValue("removeImage", true);
  };

  const setCountryList = () => {
    setCountries(
      countryList.map((country) => {
        return {
          label: country,
          value: country.toLocaleLowerCase(),
        };
      })
    );
  };

  const resendEmailVerification = async () => {
    let signature: string;
    try {
      signature = await signTransactionMessage(
        "Update user account data",
        payerWallet,
        values.isLedger
      );
    } catch (error: any) {
      createNotification(
        "error",
        error.message || error.error || "Signing failed!"
      );
      return null;
    }
    const resendEmailDto: IResendEmailDto = {
      resendVerification: true,
      signedMessage: signature,
      isLedger: values.isLedger,
    };

    try {
      const updatedUser: IUserAccount | undefined = await updateUserRequest(
        resendEmailDto,
        payerWallet!.publicKey!.toString()
      );
      if (updatedUser) {
        updateUser(updatedUser);
        createNotification(MESSAGE_TYPE.SUCCESS, "Email verification resent");
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <div className="user-basic-info">
      <div className="user-basic-info-wrapper">
        <div className="user-basic-info__upload-image">
          <div className="user-basic-info__image_container">
            <div className="user-basic-info__image_wrapper">
              <div className="user-basic-info__image" onClick={open}>
                {values.userImage.split("?")[0] &&
                  values.userImage.split("?")[0] !== "null" && (
                    <Fragment>
                      <img
                        className="user-basic-info__avatar"
                        src={values.userImage}
                        alt="User profile"
                      />
                      <img
                        className="user-basic-info__removeIcon"
                        src={removeIcon}
                        alt="Remove avatar"
                        onClick={removeImage}
                      />
                    </Fragment>
                  )}
              </div>
              <div>
                <p>
                  Formats: png, jpg, gif. <br /> Max size: 2 MB
                </p>
              </div>
            </div>
          </div>
          <div {...getRootProps()} className="user-basic-info__upload-actions">
            <input {...getInputProps()} />
            <button type="button" onClick={open}>
              Upload image
            </button>
          </div>
        </div>
        {/* TODO@milica-monke-merge: Monke does not have civic for now, uncomment this for main instance {
          <div className="user-basic-info__upload-image">
            <div className="user-basic-info__image_container">
              <div className="user-basic-info__image_wrapper-civic">
                <img
                  src={KYCStatusesMap[GatewayStatus[gatewayStatus]].icon}
                  alt=""
                />
                <div className="user-basic-info-wrapper-text">
                  <div className="user-basic-info-wrapper__verification">
                    <span>
                      {KYCStatusesMap[GatewayStatus[gatewayStatus]].label}
                    </span>
                    <div className="user-basic-info-wrapper__verification-chip">
                      {gatekeeperNetwork.toString() ===
                      RestrictionOptionsMap[
                        RestrictionLocationOptions.OnlyUS
                      ].gatekeeperNetwork.toString()
                        ? "US citizen"
                        : "Non-US citizen"}
                    </div>
                  </div>
                  <p>
                    Verify to unlock the possibility to join more clubs and
                    trade shares in clubs.
                  </p>
                </div>
              </div>
            </div>
            {KYCStatusesMap[GatewayStatus[gatewayStatus]].buttonLabel && (
              <div className="user-basic-info__upload-actions">
                <button type="button" onClick={triggerVerification}>
                  {KYCStatusesMap[GatewayStatus[gatewayStatus]].buttonLabel}
                </button>
              </div>
            )}
          </div>
        } */}
      </div>
      <div className="user-account-settings__wallet">
        <p className="user-account-settings__wallet-label">Wallet address</p>
        <div className="club-preview__terms-conditions">
          <CustomCheckbox
            checked={values.isLedger}
            onClickMethod={() => {
              setFieldValue("isLedger", !values.isLedger);
            }}
          />
          <p className="club-preview__terms-text">
            <b>Using Ledger</b>
          </p>
        </div>
        <div className="user-account-settings__wallet-address">
          <p>
            {payerWallet.publicKey
              ? getTrimmedPublicKey(payerWallet.publicKey!)
              : ""}
          </p>
          <div className="user-account-settings__wallet-icons">
            <a
              href={`https://solscan.io/token/${payerWallet.publicKey}`}
              target="_blank"
              rel="noreferrer"
            >
              <LinkIcon />
            </a>
            <img
              src={copyIcon}
              alt="Wallet copy icon"
              onClick={() => copyWalletAddress(payerWallet.publicKey!)}
            />
          </div>
        </div>
        <p className="user-account-settings__wallet-message">
          Your wallet address cannot be edited. Connect another wallet if you
          want to change it.
        </p>
      </div>
      <div className="user-basic-info__inputs">
        <WizardField
          type="text"
          name={usernameField.name}
          placeholder="Enter your unique name here"
          label={usernameField.label}
        />
        <div className="user-basic-info__mail-country">
          <div className="user-basic-info__email-wrapper">
            <WizardField
              type="text"
              name={emailField.name}
              placeholder="Enter your email here"
              label={emailField.label}
            />
            {isModal && (
              <div className="user-basic-info__verify-email">
                <p>Verification email will be send to your email address </p>
              </div>
            )}
            {!isModal && (
              <div className="user-basic-info__verify-email">
                <p>
                  {" "}
                  {user?.emailVerified
                    ? "Email verified"
                    : "Please verify your email address"}
                </p>
                {!user?.emailVerified && (
                  <button onClick={resendEmailVerification} type="button">
                    Resend
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="user-basic-info__country">
            <WizardField
              name={country.name}
              placeholder={country.label}
              type="select"
              selectOptions={countries}
              isSearchable
              label={country.label}
              defaultSelectValue={user?.country}
            />
          </div>
        </div>
      </div>
      {verificationModal && (
        <VerificationModal
          onClose={() => {
            setVerificationModal(false);
          }}
          submitAction={() => requestGatewayToken()}
        />
      )}
    </div>
  );
};

export default BasicInfo;
